import React, { Suspense } from 'react';
import { createNativeStackNavigator } from '@react-navigation/native-stack';

import { NavigationContainer, useNavigation } from '@react-navigation/native';
import { AppDownloadBanner } from './components/AppDownloadBanner';
import { MainScreen } from './screens/MainScreen';
import type { ScreenItem } from 'shared/src/types';
import { MarketScreens } from 'market/src/Navigator';
import { GoodsScreens } from 'goods/src/Navigator';
import { MyPageScreens } from 'mypage/src/Navigator';
import { HomeScreens, HomeTabScreens } from 'home/src/Navigator';
import { SearchScreens } from 'search/src/Navigator';
import { ScreensPageScreens } from 'screens/src/Navigator';
import { LoginScreens } from 'login/src/Navigator';
import { ModalProvider } from '@banhala/ui-kit';
import { ErrorBoundary } from 'shared/src/component';
import { LoadingView } from 'ably-app/src/components/LoadingView';
import * as Sentry from '@sentry/react';
import type { AppProps } from './App';
import { RetainedStateProvider } from 'shared/src/hooks/retainedState';
import { BottomTabNavigator } from 'home/src/navigation/BottomTabNavigation';

const Stack = createNativeStackNavigator();

const screenMetadata: ScreenItem[] = [
  ...MarketScreens,
  ...GoodsScreens,
  ...MyPageScreens,
  ...SearchScreens,
  ...ScreensPageScreens,
  ...HomeScreens,
  ...LoginScreens,
];

// const BottomTabs = createBottomTabNavigator();
//
// const BottomTabNavigator = () => {
//   return (
//     <BottomTabs.Navigator
//       screenOptions={{ headerShown: false }}
//       tabBar={props => <BottomTabBar {...props} />}
//     >
//       {HomeTabScreens.map((screen: ScreenItem) => (
//         <BottomTabs.Screen
//           name={screen.name}
//           component={screen.component}
//           options={{
//             title:
//               screen.name === 'Home'
//                 ? '에이블리 | 전상품 무료배송'
//                 : screen.options?.tabBarLabel || screen.name,
//             tabBarIcon: screen.options?.icon,
//             tabBarLabel: screen.options?.tabBarLabel,
//           }}
//         />
//       ))}
//     </BottomTabs.Navigator>
//   );
// };

// WEBVIEW 탭은 RN 화면(HomeTabScreens)이 아니라 TabWebViewScreen 을 쓰므로, 앱 라우트
// 경로(URL)를 screen_name 기준으로 여기서 지정한다.
const WEBVIEW_TAB_PATHS: Record<string, string> = {
  TODAY_DELIVERY: 'today_delivery',
  PLACE_HOME: 'place',
  MY_PAGE: 'mypage',
};

const linkingScreens = {
  tab: {
    path: '/',
    // 하단탭 route 는 mainTabs 의 screen_name 으로 등록되므로, linking 키도 screenName 으로 맞춘다.
    // (screen_name 이 없는 항목은 탭 route 로 등록되지 않으므로 제외)
    screens: {
      ...HomeTabScreens.reduce<Record<string, string>>((acc, cur) => {
        if (cur.screenName != null) {
          acc[cur.screenName] = cur.path;
        }
        return acc;
      }, {}),
      ...WEBVIEW_TAB_PATHS,
    },
  },
  ...screenMetadata.reduce<Record<string, string>>((acc, cur) => {
    acc[cur.name] = cur.path;
    return acc;
  }, {}),
};

export const NavigationWithModalProvider: React.FC<AppProps> = () => {
  return (
    <NavigationContainer
      linking={{
        config: {
          screens: linkingScreens,
        },
        prefixes: ['https://mobile.a-bly.com'],
      }}
    >
      <AsyncErrorBoundary>
        <Stack.Navigator
          screenOptions={{
            headerShown: false,
          }}
          screenLayout={({ children, route }) => {
            return (
              <RetainedStateProvider key={route.key}>
                <ModalProvider>
                  <AppDownloadBanner />
                  {children}
                </ModalProvider>
              </RetainedStateProvider>
            );
          }}
        >
          <Stack.Screen
            options={{
              headerShown: false,
            }}
            name={'tab'}
            component={BottomTabNavigator}
          />
          <Stack.Screen name={'main'} component={MainScreen} />
          {screenMetadata.map(screen => {
            return (
              <Stack.Screen
                name={screen.name}
                component={screen.component}
                options={screen.options}
              />
            );
          })}
        </Stack.Navigator>
      </AsyncErrorBoundary>
    </NavigationContainer>
  );
};

const AsyncErrorBoundary = ({ children }: { children: React.ReactNode }) => {
  const navigator = useNavigation();
  return (
    <ErrorBoundary
      navigation={navigator}
      onReportError={(error, errorInfo) => {
        Sentry.captureException(error, {
          contexts: { react: { componentStack: errorInfo.componentStack } },
        });
      }}
    >
      <Suspense fallback={<LoadingView />}>{children}</Suspense>
    </ErrorBoundary>
  );
};
